import { createContext, FC, useCallback, useMemo, useRef, useState } from 'react'
import PageLayout from '../../components/page-layout/page-layout'
import { ConfigProvider, Flex, Form, Pagination, Space, Table } from 'antd'
import { useForm } from 'antd/es/form/Form'
import { EditableCell } from '../../components/editable-cell/editable-cell'
import { setEditing } from './helpers/table-helpers'
import AddNewCalculation from './components/add-new-calculation/add-new-calculation'
import { calculationsApi, useGetMyCalculationsQuery } from '../../store/api/calculations-api'
import { buildDataSourceWithSharing } from './helpers/convert-calculations-to-data-source'
import useEndpointQueryParams from '../../hooks/table-pages/use-endpoint-query-parameters'
import CalculationPosition, {
    type CalculationPositionComponentKey,
} from '../../types/api/calculation-position'
import { useUpdateCalculationPositionMutation } from '../../store/api/calculation-positions-api'
import { useAppDispatch } from '../../store/store'
import {
    beginEditingPosition,
    endEditingPosition,
} from '../../store/calculations/editing-positions-slice'
import Calculation from '../../types/api/calculation'
import CurrencyRates from './components/currency-rates/currency-rates'
import ShipmentCreationForm from './components/shipment-creation-form/shipment-creation-form'
import { CalculationsTableWrapper, ModalStyled } from './elements/elements'
import { getRowClassName, getRowStyle } from './helpers/row-helpers'
import useTablePagination from '../../hooks/table-pages/use-table-pagination'
import HelpDrawer from './components/help-drawer/help-drawer'
import { calculationPositionsQuery } from './constants/calculation-positions-query'
import { defaultParams } from './constants/default-params'
import { useErrorModal } from '../../hooks/api/use-error-modal'
import { deserializeFilters } from './helpers/deserialize-filters'
import { configProviderTheme } from './constants/config-provider-theme'
import { columns } from './constants/columns'
import { NamePath } from 'antd/es/form/interface'
import FilterForm from './components/filters-form/filters-forms'
import { getDataForUpdate } from '../../helpers/tables/get-data-for-update'
import useFiltersForm from '../../hooks/forms/use-filters-form'

type FormValuesSingle = [NamePath, any]
type FormValuesMulti = { list: FormValuesSingle[] }
type FormValues = FormValuesSingle | FormValuesMulti

/** Ключи-компоненты позиции расчёта: при обновлении поля мержим с существующим и передаём id. При добавлении нового компонента в CalculationPosition — добавить ключ сюда. */
const CALC_POS_COMPONENT_KEYS = [
    'virtualStamp',
    'calculated',
] satisfies readonly CalculationPositionComponentKey[]

export type CalculationToolingKind = 'stamp' | 'forms'

interface CalculationPageCtxValue {
    onOpenDesignerTaskModal: (calc: Calculation) => void
    onUpdateCalculationPositionProperty: (
        calcPos: CalculationPosition,
        formValues: FormValues
    ) => void
    onSetToolingIncluded: (
        calcPos: CalculationPosition,
        kind: CalculationToolingKind,
        included: boolean
    ) => Promise<void>
}

export const CalculationPageCtx = createContext<CalculationPageCtxValue>({
    onOpenDesignerTaskModal: () => {},
    onUpdateCalculationPositionProperty: () => {},
    onSetToolingIncluded: async () => {},
})

interface CalculationsPageProps {}

const CalculationsPage: FC<CalculationsPageProps> = () => {
    const params = useEndpointQueryParams({ deserializeFilters })

    const queryArg = useMemo(() => ({ ...params, ...defaultParams }), [params])
    const { data } = useGetMyCalculationsQuery(queryArg)
    const pagination = useTablePagination(queryArg, data)
    const { form: filterForm, onSearch, onClear } = useFiltersForm()

    const prevDataRef = useRef<Calculation[] | null>(null)
    const prevDataSourceRef = useRef<ReturnType<typeof buildDataSourceWithSharing>>([])

    const dataSource = useMemo(() => {
        const current = data?.data ?? []
        const next = buildDataSourceWithSharing(
            current,
            prevDataRef.current,
            prevDataSourceRef.current
        )
        prevDataRef.current = current
        prevDataSourceRef.current = next
        return next
    }, [data])

    const [openModalOfTaskForDesigner, setOpenModalOfTaskForDesigner] = useState<boolean>(false)
    const [calcForTaskOfDesigner, setCalcForTaskOfDesigner] = useState<Calculation>()

    const [updateCalculationPosition, updateCalculationPositionState] =
        useUpdateCalculationPositionMutation()

    useErrorModal(updateCalculationPositionState, 'Ошибка обновления позиции расчета')

    const dispatch = useAppDispatch()
    const [form] = useForm()
    // @ts-ignore
    window.cform = form

    const closeModal = () => setOpenModalOfTaskForDesigner(false)

    const onOpenDesignerTaskModal = (calc: Calculation) => {
        setCalcForTaskOfDesigner(calc)
        setOpenModalOfTaskForDesigner(true)
    }

    const updateQueryData = useCallback(
        (calculationPosition: CalculationPosition) => {
            const action = calculationsApi.util.updateQueryData(
                'getMyCalculations',
                queryArg,
                draft => {
                    draft.data.some(calculation => {
                        const positions = calculation.calculation_positions || []

                        const index = positions.findIndex(cp => {
                            return cp.documentId === calculationPosition.documentId
                        })

                        if (index !== -1) {
                            positions[index] = calculationPosition
                            return true
                        }

                        return false
                    })
                }
            )

            dispatch(action)
        },
        [dispatch, queryArg]
    )

    /**
     * Функция, которая по факту вызывается из EditableCell, и обновляет данные в таблице
     */
    const onUpdateCalculationPositionProperty = useCallback(
        async (record: CalculationPosition, formValues: FormValues) => {
            try {
                // TODO: чекать изменились ли свойства, может запрос делать и не нужно
                const data = getDataForUpdate(record, formValues, {
                    componentKeys: CALC_POS_COMPONENT_KEYS,
                })

                const res = await updateCalculationPosition({
                    docId: record.documentId,
                    body: { data },
                    queryParams: calculationPositionsQuery,
                })

                if (res.data) {
                    updateQueryData(res.data.data)
                }
            } catch {
                // ошибка обрабатывается хуком useErrorModal
            }
        },
        [updateCalculationPosition, updateQueryData]
    )

    const onSetToolingIncluded = useCallback(
        async (
            record: CalculationPosition,
            kind: CalculationToolingKind,
            included: boolean
        ) => {
            const data =
                kind === 'stamp'
                    ? { stampIncludedInCost: included }
                    : { formsIncludedInCost: included }
            const res = await updateCalculationPosition({
                docId: record.documentId,
                body: { data },
                queryParams: calculationPositionsQuery,
            })

            if (res.data) {
                updateQueryData(res.data.data)
            }
        },
        [updateCalculationPosition, updateQueryData]
    )

    const onEditingSessionChange = useCallback(
        (documentId: string, active: boolean) => {
            dispatch(active ? beginEditingPosition(documentId) : endEditingPosition(documentId))
        },
        [dispatch]
    )

    const columnsForRender = useMemo(
        () => setEditing(columns, onUpdateCalculationPositionProperty, onEditingSessionChange),
        [onUpdateCalculationPositionProperty, onEditingSessionChange]
    )

    const tableComponents = useMemo(() => ({ body: { cell: EditableCell } }), [])

    const onRow = useCallback((record: Parameters<typeof getRowStyle>[0]) => {
        const style = getRowStyle(record)
        return style ? { style } : {}
    }, [])

    const headerRowProps = useCallback(() => ({ style: { lineHeight: 1.2 } }), [])

    const tableFooter = useCallback(
        () => <Pagination {...pagination} size='small' align='end' />,
        [pagination]
    )

    const getRowKey = useCallback((x: { documentId?: string }) => x.documentId ?? '', [])

    const calc = calcForTaskOfDesigner

    return (
        <CalculationPageCtx.Provider
            value={{
                onOpenDesignerTaskModal,
                onUpdateCalculationPositionProperty,
                onSetToolingIncluded,
            }}
        >
            <PageLayout title={'Расчеты'} beforeUser={<HelpDrawer />}>
                <Flex gap={24} align={'center'} justify={'space-between'}>
                    <Space size={24}>
                        <CurrencyRates />
                        <FilterForm form={filterForm} onSearch={onSearch} onClear={onClear} />
                    </Space>
                    <AddNewCalculation />
                </Flex>
                <Form style={{ flex: 'auto' }} form={form} component={false}>
                    <ConfigProvider theme={configProviderTheme}>
                        <CalculationsTableWrapper style={{ flex: 'auto' }} filtersSummaryHeight={0}>
                            <Table
                                bordered
                                columns={columnsForRender}
                                dataSource={dataSource}
                                components={tableComponents}
                                rowKey={getRowKey}
                                pagination={false}
                                size={'small'}
                                onHeaderRow={headerRowProps}
                                rowClassName={getRowClassName}
                                onRow={onRow}
                                footer={tableFooter}
                            />
                        </CalculationsTableWrapper>
                    </ConfigProvider>
                </Form>

                <ModalStyled
                    width={1480}
                    open={openModalOfTaskForDesigner}
                    footer={false}
                    onCancel={closeModal}
                    destroyOnClose
                    // TODO: поэкспериментировать с кей, чтобы если форма открылась с тем же расчетом, то все данные оставались в ней, а если с другим, то ререндер, возможно брать ключом id
                >
                    {calc ? (
                        <ShipmentCreationForm
                            calculation={calc}
                            onSuccess={closeModal}
                            onCalculationPositionUpdated={updateQueryData}
                        />
                    ) : null}
                </ModalStyled>
            </PageLayout>
        </CalculationPageCtx.Provider>
    )
}

export default CalculationsPage
